import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Spinner } from 'reactstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { Button } from '~components';
import { AuthTags } from '~constants/authTags';

import {
  useGetDexDetailsQuery,
  useUpsertDexDetailsMutation,
  DexDetails,
  DexDetailsBody,
} from '~appointments/actions/dexDetails';
import {
  DEX_FIELD_COMPONENTS,
  DexFieldProps,
  DexFieldTag,
  DexFormValues,
  isDexFieldTag,
  MEAL_GATED_TAGS,
} from '~appointments/components/dexFields';

const hasTag = (tags: string[], tag: DexFieldTag) => tags.includes(tag);

// The meal-detail fields (texture_modified + meal counts) always show unless the tag list also
// carries meals_provided (Respite), in which case they only show once meals_provided is checked.
const mealDetailsVisible = (tags: string[], mealsProvided: boolean) =>
  !hasTag(tags, 'meals_provided') || mealsProvided;

const buildValidationSchema = (fieldTags: string[]) => {
  const numericField = () =>
    Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .min(0, 'Must be 0 or greater');

  const hasMealType = hasTag(fieldTags, 'meal_type');
  const mealGatedByProvided = hasTag(fieldTags, 'meals_provided');

  const mealCountField = () => {
    if (!hasMealType) return numericField();
    if (!mealGatedByProvided) return numericField().required('Required');
    return numericField().when('meals_provided', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema,
    });
  };

  return Yup.object({
    subcontractor: hasTag(fieldTags, 'subcontractor')
      ? Yup.string().required('Subcontractor is required')
      : Yup.string(),
    session_location: hasTag(fieldTags, 'session_location')
      ? Yup.string().required('Session Location is required')
      : Yup.string(),
    meal_type_main: mealCountField(),
    meal_type_light: mealCountField(),
    meal_type_dessert: mealCountField(),
    meal_type_beverage: mealCountField(),
    meal_type_snack: mealCountField(),
    engagement_time_minutes: numericField(),
    trip_distance_km: numericField(),
    trip_travel_time_minutes: numericField(),
  });
};

interface ComponentProps {
  appointmentId: string | number;
  isActive?: boolean;
}

export const toFormValues = (data: Partial<DexDetailsBody> | null): DexFormValues => ({
  subcontractor: data?.subcontractor != null ? String(data.subcontractor) : '',
  volunteer: data?.volunteer ?? false,
  session_location: data?.session_location != null ? String(data.session_location) : '',
  overnight: data?.overnight ?? false,
  meals_provided: data?.meals_provided ?? false,
  meals_cooked_offsite: data?.meals_cooked_offsite ?? false,
  texture_modified: data?.texture_modified ?? false,
  meal_type_main: data?.meal_type_main != null ? String(data.meal_type_main) : '',
  meal_type_light: data?.meal_type_light != null ? String(data.meal_type_light) : '',
  meal_type_dessert: data?.meal_type_dessert != null ? String(data.meal_type_dessert) : '',
  meal_type_beverage: data?.meal_type_beverage != null ? String(data.meal_type_beverage) : '',
  meal_type_snack: data?.meal_type_snack != null ? String(data.meal_type_snack) : '',
  engagement_time_minutes: data?.engagement_time_minutes != null ? String(data.engagement_time_minutes) : '',
  trip_distance_km: data?.trip_distance_km != null ? String(data.trip_distance_km) : '',
  trip_travel_time_minutes: data?.trip_travel_time_minutes != null ? String(data.trip_travel_time_minutes) : '',
});

// Only persist values for the tags the resolved DexID actually maps to; everything else is reset to
// its default so a service-type reassignment cannot leave stale field values behind.
export const toApiBody = (values: DexFormValues, fieldTags: string[]): Partial<DexDetailsBody> => {
  const has = (tag: DexFieldTag) => hasTag(fieldTags, tag);
  const includeMealDetails = has('meal_type') && mealDetailsVisible(fieldTags, values.meals_provided);
  const toNumber = (value: string) => (value !== '' ? Number(value) : null);
  return {
    subcontractor: has('subcontractor') ? values.subcontractor === 'true' : false,
    volunteer: has('volunteer') ? values.volunteer : false,
    session_location: has('session_location') && values.session_location !== '' ? Number(values.session_location) : null,
    overnight: has('overnight') ? values.overnight : false,
    meals_provided: has('meals_provided') ? values.meals_provided : false,
    meals_cooked_offsite: has('meals_cooked_offsite') ? values.meals_cooked_offsite : false,
    texture_modified: includeMealDetails ? values.texture_modified : false,
    meal_type_main: includeMealDetails ? toNumber(values.meal_type_main) : null,
    meal_type_light: includeMealDetails ? toNumber(values.meal_type_light) : null,
    meal_type_dessert: includeMealDetails ? toNumber(values.meal_type_dessert) : null,
    meal_type_beverage: includeMealDetails ? toNumber(values.meal_type_beverage) : null,
    meal_type_snack: includeMealDetails ? toNumber(values.meal_type_snack) : null,
    engagement_time_minutes: has('engagement_time') ? toNumber(values.engagement_time_minutes) : null,
    trip_distance_km: has('trip_distance') ? toNumber(values.trip_distance_km) : null,
    trip_travel_time_minutes: has('trip_travel_time') ? toNumber(values.trip_travel_time_minutes) : null,
  };
};

const DexResetOnDirty: React.FC<{ dirty: boolean; onDirty: () => void }> = ({ dirty, onDirty }) => {
  useEffect(() => {
    if (dirty) onDirty();
  }, [dirty, onDirty]);
  return null;
};

const AppointmentDexTab: React.FC<ComponentProps> = ({ appointmentId, isActive = true }) => {
  const { data: dexData, isLoading, isFetching, isError } = useGetDexDetailsQuery(
    { appointmentId },
    { skip: !isActive },
  );

  // `skip` makes RTK Query select a distinct cache key, so `dexData` reverts to undefined the
  // instant the tab goes inactive — not just "not yet fetched". Retaining the last successfully
  // fetched value keeps `initialValues` stable across tab switches so Formik's enableReinitialize
  // doesn't wipe an unsaved draft when the user tabs away and back.
  const [lastDexData, setLastDexData] = useState<DexDetails | null>(null);
  useEffect(() => {
    if (dexData !== undefined) {
      setLastDexData(dexData);
    }
  }, [dexData]);

  const [upsertDexDetails, { isLoading: isSaving, isSuccess: isSaveSuccess, isError: isSaveError, reset }] =
    useUpsertDexDetailsMutation();

  const fieldTags = useMemo(() => lastDexData?.field_tags ?? [], [lastDexData?.field_tags]);
  const isExcluded = lastDexData?.is_excluded === true;
  const dexFound = lastDexData?.dex_found === true;

  const initialValues = toFormValues(lastDexData ?? null);
  const validationSchema = useMemo(() => buildValidationSchema(fieldTags), [fieldTags]);

  const handleSubmit = async (values: DexFormValues, { resetForm }: FormikHelpers<DexFormValues>) => {
    const result = await upsertDexDetails({ appointmentId, ...toApiBody(values, fieldTags) });
    if (!('error' in result)) {
      resetForm({ values });
    }
  };

  if ((isLoading || isFetching) && lastDexData === null) {
    return (
      <div className="p-4 text-center">
        <Spinner size="sm" /> Loading DEX details...
      </div>
    );
  }

  if (isError) {
    return <Alert color="danger">Failed to load DEX details. Please refresh and try again.</Alert>;
  }

  if (isExcluded) {
    return (
      <Alert color="info" data-cy="dex-excluded-message">
        This service type is excluded from DEX export.
      </Alert>
    );
  }

  if (!dexFound) {
    return (
      <Alert color="danger" data-cy="dex-config-missing-message">
        DEX configuration is missing for this appointment&apos;s service type.
      </Alert>
    );
  }

  const renderTag = (tag: string, fieldProps: DexFieldProps) => {
    if (!isDexFieldTag(tag)) return null;
    if (MEAL_GATED_TAGS.includes(tag) && !mealDetailsVisible(fieldTags, fieldProps.values.meals_provided)) {
      return null;
    }
    const FieldComponent = DEX_FIELD_COMPONENTS[tag];
    return <FieldComponent key={tag} {...fieldProps} />;
  };

  return (
    <div className="p-3 appointment-dex-tab" data-cy="appointment-dex-tab">
      {isSaveSuccess && (
        <Alert color="success" data-cy="dex-save-success">
          DEX details saved.
        </Alert>
      )}
      {isSaveError && (
        <Alert color="danger" data-cy="dex-save-error">
          Failed to save DEX details. Please try again.
        </Alert>
      )}
      {isFetching && lastDexData !== null && (
        <div className="text-muted small mb-2" data-cy="dex-refreshing-indicator">
          <Spinner size="sm" /> Refreshing DEX details...
        </div>
      )}

      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, dirty, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, isSubmitting }) => (
          <Form onSubmit={formikSubmit} data-cy="dex-form">
            <DexResetOnDirty dirty={dirty} onDirty={reset} />
            {dirty && (
              <Alert color="warning" className="py-1 px-2 mb-3" data-cy="dex-unsaved-indicator">
                Unsaved changes
              </Alert>
            )}

            {fieldTags.map((tag) => renderTag(tag, { values, errors, touched, handleChange, handleBlur }))}

            <div className="mt-4">
              <Button
                type="submit"
                color="success"
                disabled={!dirty || isSubmitting || isSaving}
                authTag={AuthTags.APPOINTMENTS.UPDATE}
                dataCy="dex-save-button"
              >
                {isSaving ? 'Saving...' : 'Save DEX Details'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AppointmentDexTab;
